import React, { useState } from "react";
import { Typography, Box, Chip, Collapse, Stack } from "@mui/material";
import { motion } from "framer-motion";

const experiences = [
    {
        role: "Senior Data and AI Engineer (L6)",
        team: "AMZL Field Support",
        company: "Amazon",
        location: "Vancouver, BC",
        years: "Dec 2024 – Present",
        accent: "#0077ff",
        award: "Extra Miler ×2",
        highlights: [
            "Designed and built AskOrbit, a production conversational AI agent answering SPR, OTR, and operational queries for 400+ AMZL sites. Reduced response time from 30–96s to ~14s via a three-layer caching architecture.",
            "Chose Strands over LangChain/LangGraph/Bedrock multi-agent after structured evaluation across 6+ dimensions; wrote full HLD and caching strategy design docs.",
            "Combined Neptune knowledge graph with real-time data tools for hybrid retrieval achieving sub-100ms query response.",
            "Built BIE onboarding playbook enabling domain experts to add knowledge without engineering support, cutting agent onboarding from weeks to 3 days.",
            "Authored multi-agent supervisor vision doc and built working model to drive alignment across PerfectMile and EPS teams; built Streamlit POC and Slack integration for VP-level demo.",
            "Created integration testing framework with scenario-based evaluation (tool-call assertions, date handling) for production reliability.",
        ],
        tags: ["Strands", "Neptune", "Bedrock", "Python", "AWS", "RAG", "Knowledge Graphs"],
    },
    {
        role: "Senior Data and AI Engineer",
        team: "Applied AI & QuBIT",
        company: "Amazon",
        location: "Vancouver, BC",
        years: "Apr 2024 – Nov 2024",
        accent: "#f59e0b",
        highlights: [
            "Built end-to-end MLOps platform for PyTorch vision transformer models with CI/CD, automated A/B testing, human-in-the-loop approval gates, and auto-rollback, delivering $5.5M in annual savings.",
            "Reduced model deployment cycle from 2 weeks to 2 days. Improved model accuracy by 15%.",
            "Built React-based self-serve model management UI with Cognito auth; adopted as standard across Applied AI (10 models, 3 teams).",
            "Designed and delivered Optima, a configuration-driven, event-sourced data platform (S3/SQS/Kinesis/EventBridge → Apache Iceberg) that cut new dataset onboarding by 96% and enabled hourly tracking of 100K+ container movements.",
        ],
        tags: ["SageMaker", "PyTorch", "CI/CD", "Iceberg", "Kinesis", "CDK", "React"],
    },
    {
        role: "Software Engineer",
        team: "Emerging Country Growth",
        company: "Amazon",
        location: "Vancouver, BC",
        years: "Sep 2021 – Mar 2024",
        accent: "#ff6b35",
        highlights: [
            "Led zero-downtime migration of Atlas AI data layer from DynamoDB to OpenSearch across 400 production sites, 70% latency reduction (300ms → 90ms), 99.6% availability maintained, 3× load headroom built in.",
            "Evaluated Redshift Serverless, Athena, and OpenSearch; chose OpenSearch based on latency and query flexibility. Designed real-time stream pipeline with feature flags and rollback levers.",
            "Created reusable CDK constructs reducing future onboarding by 1.5 dev hours per construct.",
            "Reduced reporting latency by 91% via event-driven architecture using Kinesis, Firehose, and Apache Iceberg.",
            "Improved ETL test coverage by 79%, reducing critical production bugs by 64%.",
        ],
        tags: ["OpenSearch", "DynamoDB", "Kinesis", "CDK", "Python", "Route 53"],
    },
    {
        role: "Senior Data Engineer",
        team: "Data Platform",
        company: "PepsiCo",
        location: "San Francisco, CA",
        years: "Jul 2018 – May 2021",
        accent: "#00e5a0",
        highlights: [
            "Built enterprise-wide self-serve analytics platform with reusable Airflow operator/hook/sensor library, reduced pipeline development effort by 77%.",
            "Designed DBT-based serving layer with CI/CD; improved data quality across business units by 16%.",
            "Enabled Kubernetes-orchestrated horizontal scaling for growing data volumes.",
        ],
        tags: ["Airflow", "DBT", "Kubernetes", "Python", "CI/CD"],
    },
    {
        role: "Software Engineer",
        team: "Financial Systems",
        company: "Royal Bank of Canada",
        location: "India",
        years: "Dec 2013 – May 2016",
        accent: "#8a92a0",
        highlights: [
            "Financial backend systems engineering, CI/CD pipeline implementation, and performance optimization.",
            "Improved test coverage to 90% and reduced deployment time by 40% via CI/CD automation.",
        ],
        tags: ["Java", "CI/CD", "Performance Engineering"],
    },
];

const ExperienceCard: React.FC<{ exp: typeof experiences[0]; index: number }> = ({ exp, index }) => {
    const [open, setOpen] = useState(index === 0);

    return (
        <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: index * 0.07 }}
        >
            <Box
                onClick={() => setOpen(!open)}
                sx={{
                    border: "1px solid",
                    borderColor: open ? "rgba(255,255,255,0.13)" : "divider",
                    borderLeft: `3px solid ${exp.accent}`,
                    borderRadius: 2,
                    p: 2.5,
                    cursor: "pointer",
                    bgcolor: open ? "background.paper" : "transparent",
                    transition: "all 0.2s",
                    "&:hover": { bgcolor: "background.paper", borderColor: "rgba(255,255,255,0.13)" },
                }}
            >
                {/* Header */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2 }}>
                    <Box>
                        <Typography
                            sx={{
                                fontFamily: "'DM Mono', monospace",
                                fontSize: "11px",
                                color: exp.accent,
                                letterSpacing: "0.08em",
                                mb: 0.4,
                            }}
                        >
                            {exp.company} · {exp.team}
                        </Typography>
                        <Typography variant="h6" sx={{ letterSpacing: "-0.01em", lineHeight: 1.2, mb: 0.3 }}>
                            {exp.role}
                        </Typography>
                        <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "11px", color: "text.secondary" }}>
                            {exp.location}
                        </Typography>
                    </Box>
                    <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                        <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "11px", color: "text.secondary", whiteSpace: "nowrap" }}>
                            {exp.years}
                        </Typography>
                        {exp.award && (
                            <Box
                                sx={{
                                    mt: 0.75,
                                    display: "inline-block",
                                    fontFamily: "'DM Mono', monospace",
                                    fontSize: "10px",
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: 1,
                                    bgcolor: "rgba(0,119,255,0.1)",
                                    border: "1px solid rgba(0,119,255,0.25)",
                                    color: "primary.main",
                                }}
                            >
                                {exp.award}
                            </Box>
                        )}
                    </Box>
                </Box>

                {/* Collapsible highlights */}
                <Collapse in={open}>
                    <Box sx={{ mt: 2 }}>
                        <Box component="ul" sx={{ pl: 2, mb: 1.5, "& li": { mb: 0.75 } }}>
                            {exp.highlights.map((h, i) => (
                                <Typography component="li" key={i} variant="body2" sx={{ color: "text.secondary", lineHeight: 1.65 }}>
                                    {h}
                                </Typography>
                            ))}
                        </Box>
                        <Stack direction="row" flexWrap="wrap" gap={0.75}>
                            {exp.tags.map((tag) => (
                                <Chip key={tag} label={tag} size="small" variant="outlined" />
                            ))}
                        </Stack>
                    </Box>
                </Collapse>
            </Box>
        </motion.div>
    );
};

const Experience: React.FC = () => (
    <Box>
        <Typography
            sx={{
                fontFamily: "'DM Mono', monospace",
                fontSize: "11px",
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                color: "primary.main",
                mb: 0.75,
            }}
        >
            Work
        </Typography>
        <Typography variant="h4" sx={{ mb: 3, letterSpacing: "-0.02em" }}>
            Experience
        </Typography>

        <Stack spacing={1.5}>
            {experiences.map((exp, i) => (
                <ExperienceCard key={i} exp={exp} index={i} />
            ))}
        </Stack>
    </Box>
);

export default Experience;
